import { Fragment, useMemo, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/utils/number';
import type { PcmpPendingReport, PcmpPendingRow } from '../../types/pcmpReports.types';

const cellClass = 'border border-neutral-300 px-2 py-1';
const headClass = cn(cellClass, 'bg-neutral-100 text-center font-semibold text-neutral-900');
const numClass = 'text-right tabular-nums whitespace-nowrap';

const amount = (value: number) =>
  value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

function Th({ children, ...rest }: { children: ReactNode; colSpan?: number; rowSpan?: number }) {
  return (
    <th {...rest} className={headClass}>
      {children}
    </th>
  );
}

interface LaneGroup {
  lane: string;
  label: string;
  span: number;
}

/** The columns' consecutive runs of one lane, for the top header row. */
function groupLanes(columns: PcmpPendingReport['columns']): LaneGroup[] {
  const groups: LaneGroup[] = [];
  for (const column of columns) {
    const last = groups[groups.length - 1];
    if (last?.lane === column.lane) last.span += 1;
    else groups.push({ lane: column.lane, label: column.laneLabel, span: 1 });
  }
  return groups;
}

function DataRow({
  row,
  columns,
  tone,
}: {
  row: PcmpPendingRow;
  columns: PcmpPendingReport['columns'];
  tone: 'row' | 'subtotal' | 'total';
}) {
  return (
    <tr
      className={cn(
        tone === 'subtotal' && 'bg-neutral-50 font-semibold',
        tone === 'total' && 'bg-neutral-200 font-bold',
      )}
    >
      <td className={cn(cellClass, 'whitespace-nowrap', tone === 'row' && 'pl-4')}>{row.region}</td>
      {columns.map((column) => {
        const cell = row.cells[column.key];
        return (
          <Fragment key={column.key}>
            <td className={cn(cellClass, numClass)}>{formatNumber(cell?.qty)}</td>
            <td className={cn(cellClass, numClass)}>{amount(cell?.amount ?? 0)}</td>
          </Fragment>
        );
      })}
    </tr>
  );
}

interface PcmpPendingReportTableProps {
  report: PcmpPendingReport;
  className?: string;
}

/** Pending proposals by division and region: a block per division, its total, then the grand total. */
export function PcmpPendingReportTable({ report, className }: PcmpPendingReportTableProps) {
  const { columns, divisions, grandTotal } = report;
  const lanes = useMemo(() => groupLanes(columns), [columns]);

  return (
    <table className={cn('w-full border-collapse text-xs', className)}>
      <thead>
        <tr>
          <Th rowSpan={3}>Region</Th>
          {lanes.map((lane) => (
            <Th key={lane.lane} colSpan={lane.span * 2}>
              {lane.label}
            </Th>
          ))}
        </tr>
        <tr>
          {columns.map((column) => (
            <Th key={column.key} colSpan={2}>
              {column.label}
            </Th>
          ))}
        </tr>
        <tr>
          {columns.map((column) => (
            <Fragment key={column.key}>
              <Th>Qty</Th>
              <Th>Amount</Th>
            </Fragment>
          ))}
        </tr>
      </thead>
      <tbody>
        {divisions.map((division) => (
          <Fragment key={division.division ?? 'unmapped'}>
            <tr>
              <td
                colSpan={1 + columns.length * 2}
                className={cn(cellClass, 'bg-neutral-100 font-semibold')}
              >
                {division.label}
              </td>
            </tr>
            {division.rows.map((row) => (
              <DataRow
                key={row.departmentId ?? `unassigned-${row.region}`}
                row={row}
                columns={columns}
                tone="row"
              />
            ))}
            <DataRow
              row={{ ...division.subtotal, region: `${division.label} Total` }}
              columns={columns}
              tone="subtotal"
            />
          </Fragment>
        ))}
        <DataRow row={grandTotal} columns={columns} tone="total" />
      </tbody>
    </table>
  );
}
